/**
 * Quem respondeu (2026-09-29) — coluna "Respondido por" da tabela de respostas
 * do relatório do formulário e as colunas equivalentes da exportação.
 *
 * A resposta guarda o contato (`contactId`): a de lead nasce com o contato do
 * lead, a do link público com o contato digitado quando o formulário pede os
 * dados. O backend devolve nome/telefone/e-mail em `respondent`. O nome abre o
 * lead no CRM (resposta ligada a lead) e o botão ao lado inicia uma conversa
 * com o contato. Sem contato: link público = "Anônimo"; resposta anterior a
 * 29/09 = "—".
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FormSubmissionResponse } from '@troia-v3/types';
import { usePermissions } from '@/hooks/usePermissions';
import { StartConversationDialog } from '@/modules/chat/components/StartConversationDialog';
import { Icon } from '@/shared/icons';
import { formatPhoneNumber } from '@/shared/utils/phone';

const isPublicLink = (submission: FormSubmissionResponse): boolean =>
  submission.source === undefined || submission.source === 'public_link';

/** Nome de quem respondeu, como aparece na tabela e na exportação. */
export const respondentName = (submission: FormSubmissionResponse): string => {
  if (submission.respondent?.name) return submission.respondent.name;
  if (submission.respondent?.phone) return formatPhoneNumber(submission.respondent.phone);
  if (submission.respondent?.email) return submission.respondent.email;
  if (submission.contactId === undefined && isPublicLink(submission)) return 'Anônimo';
  return '—';
};

/** Colunas da exportação: nome, telefone e e-mail de quem respondeu. */
export const RESPONDENT_CSV_HEADERS = ['Respondido por', 'Telefone', 'E-mail'];

export const respondentCsvValues = (submission: FormSubmissionResponse): string[] => [
  respondentName(submission),
  submission.respondent?.phone ? formatPhoneNumber(submission.respondent.phone) : '',
  submission.respondent?.email ?? '',
];

export const FormRespondentCell: React.FC<{ submission: FormSubmissionResponse }> = ({ submission }) => {
  const { hasModulePermission } = usePermissions();
  const [conversaAberta, setConversaAberta] = useState(false);

  const nome = respondentName(submission);
  const respondent = submission.respondent;
  const telefone = respondent?.phone ? formatPhoneNumber(respondent.phone) : '';
  const comContato = submission.contactId !== undefined && respondent !== undefined;
  const podeConversar = comContato && hasModulePermission('chat', 'create');

  return (
    <div className="flex items-center gap-2 min-w-[160px] max-w-[260px]" data-testid={`form-report-respondent-${submission.id}`}>
      <div className="min-w-0 flex-1">
        {submission.leadId && comContato ? (
          <Link
            to={`/crm?leadId=${submission.leadId}`}
            className="font-medium truncate block hover:underline"
            style={{ color: 'var(--theme-primary)' }}
            title="Abrir o lead no CRM"
            data-testid={`form-report-respondent-link-${submission.id}`}
          >
            {nome}
          </Link>
        ) : (
          <span
            className="truncate block"
            style={{ color: comContato ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
          >
            {nome}
          </span>
        )}
        {telefone && respondent?.name && (
          <span className="text-xs tabular-nums truncate block" style={{ color: 'var(--theme-muted-text)' }}>
            {telefone}
          </span>
        )}
      </div>
      {podeConversar && (
        <button
          type="button"
          onClick={() => setConversaAberta(true)}
          className="p-1.5 rounded-lg flex-none transition-opacity hover:opacity-70"
          style={{ color: 'var(--theme-primary)' }}
          title="Iniciar conversa"
          aria-label="Iniciar conversa"
          data-testid={`form-report-start-conversation-${submission.id}`}
        >
          <Icon name="message-circle" className="w-4 h-4" />
        </button>
      )}
      {/* Montada só aberta: a janela busca os canais, e a tabela tem várias linhas. */}
      {conversaAberta && submission.contactId !== undefined && (
        <StartConversationDialog
          open={conversaAberta}
          onOpenChange={setConversaAberta}
          contact={{
            id: submission.contactId,
            name: respondent?.name ?? nome,
            identifiers: {
              phone: respondent?.phone ? [respondent.phone] : [],
              email: respondent?.email ? [respondent.email] : [],
            },
          }}
        />
      )}
    </div>
  );
};
